import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { user } from '@adc/blueprint'
import { Text, List, PhoneVerification, hooks } from '@adc/ui-core'
import analytics from '@adc/analytics'
import classnames from 'classnames/bind'
import { optInSMSText } from '../smsOptInToggle'
import { useApplicationModals } from '../auctionApplication/modals'
import styles from './styles.scss'
import { genericErrorMessage } from '../../constants/errorMessages'

const { useWebApplicationNudges } = hooks
const { track, eventNames } = analytics

const cx = classnames.bind(styles)

const PostSaveOptInModal = ({
  open,
  onConfirm,
  onClose,
  partyId,
  latestPhone,
  onVerificationSuccess,
  isForeclosureOnline,
  trackingInfo = {},
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const elmId = 'post_save_confirm_modal'
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(
    !latestPhone.verified
  )
  const [latestPhoneNumber, setLatestPhoneNumber] = useState(latestPhone.number)

  const applicationModals = useApplicationModals()

  useEffect(() => {
    if (latestPhone.verified) {
      setIsConfirmDisabled(false)
    }
  }, [latestPhone.verified])

  useEffect(() => {
    track(eventNames.onSaveSMSAlertsBoxLoaded, {
      ...trackingInfo,
    })
  }, [trackingInfo])

  const phoneVerificationProps = useMemo(
    () => ({
      partyId,
      isStrict: true,
      onToggle: isEditing => {
        setIsConfirmDisabled(isEditing || !latestPhone.verified)
      },
      latestPhone,
      onSuccess: newPhone => {
        setLatestPhoneNumber(newPhone.number)
        onVerificationSuccess && onVerificationSuccess(newPhone)
        setIsConfirmDisabled(false)
      },
      phoneInputHelpText: 'Verify your contact number',
    }),
    [
      partyId,
      latestPhone,
      onVerificationSuccess,
      setLatestPhoneNumber,
      setIsConfirmDisabled,
    ]
  )

  const onAcceptOptIn = useCallback(async () => {
    await user.providers.updateOptInInformation({
      phone: latestPhoneNumber,
      isOptIn: true,
    })
    onConfirm(latestPhoneNumber)
    track(eventNames.onSaveSMSSubmitClick, {
      ...trackingInfo,
      smsOptIn: true,
    })
    setIsOpen(false)
    onClose()
  }, [setIsConfirmDisabled, onConfirm, latestPhoneNumber, trackingInfo])

  const onCloseClick = useCallback(() => {
    track(eventNames.onSaveSMSSubmitClick, {
      ...trackingInfo,
      smsOptIn: false,
    })
    onClose()
  }, [trackingInfo, onClose])

  const modalBody = useMemo(() => {
    const { benefits } = optInSMSText
    const benefitsChecklist = isForeclosureOnline
      ? benefits.foreclosureOnline
      : benefits.default
    return (
      <div>
        <Text className='u-mb-2'>
          Don't let this one get away! Opt-in to essential property updates so
          you can stay informed. You’ll receive action items like these:
        </Text>
        <List bulleted className='u-mb-4'>
          {benefitsChecklist.map((benefit, index) => (
            <List.Item key={`benefit_${index}`}>
              <Text>{benefit}</Text>
            </List.Item>
          ))}
        </List>
        <div className={cx('phone-verification-container')}>
          <PhoneVerification.PhoneVerificationReduxForm
            {...phoneVerificationProps}
          />
        </div>
        <div>
          <Text size='tiny' className='u-mb-1'>
            {`By clicking “Sign Me Up” below, ${optInSMSText.consentMessageAbove}`}
          </Text>
          <Text size='tiny' className='u-mb-1'>
            Message and data rates may apply. You may opt-out and manage your
            notifications at any time. Manage property-level notifications in
            your Saved Properties tab on the dashboard.
          </Text>
        </div>
      </div>
    )
  }, [isForeclosureOnline, phoneVerificationProps])

  const modalProps = useMemo(
    () => ({
      children: modalBody,
      elmId,
      size: 'small',
      title: 'Property Saved! Get Alerts Direct to Your Mobile',
      confirmButton: {
        label: 'Sign Me Up',
        onClick: onAcceptOptIn,
        disabled: isConfirmDisabled,
      },
      cancelButton: {
        label: 'Remind Me Later',
        onClick: onCloseClick,
      },
      formatConfirmError: () => {
        return `${genericErrorMessage} Also note, you can also opt into text alerts from your Account Information page at any time.`
      },
    }),
    [modalBody, elmId, onAcceptOptIn, onCloseClick, isConfirmDisabled]
  )

  const displayModal = useCallback(() => {
    if (!isOpen) {
      applicationModals.open('confirmation', modalProps)
      setIsOpen(true)
    } else {
      applicationModals.update(modalProps)
    }
  }, [
    isOpen,
    applicationModals.open,
    applicationModals.update,
    setIsOpen,
    modalProps,
  ])

  const hideModal = useCallback(() => {
    applicationModals.close()
    setIsOpen(false)
  }, [applicationModals.close, setIsOpen])

  useEffect(() => {
    if (open) {
      displayModal()
    } else {
      hideModal()
    }
  }, [open, displayModal, hideModal])

  return <div />
}

PostSaveOptInModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  partyId: PropTypes.string.isRequired,
  latestPhone: PropTypes.shape({
    number: PropTypes.string,
    opt_in: PropTypes.bool,
    verified: PropTypes.bool,
  }),
  onVerificationSuccess: PropTypes.func,
  isForeclosureOnline: PropTypes.bool,
  trackingInfo: PropTypes.object,
}

export { useWebApplicationNudges }

export default PostSaveOptInModal
